<template>
  <div class="topBar relative flex bg-layer-2 px-4 md:px-6 py-[10px] h-[82px] items-center justify-between z-[100]">
    <div class="mr-auto hidden md:flex ">
      <Popover class="relative" v-slot="{ open }">
        <PopoverButton class="top-bar-button hover:bg-button-ghost-mono-active outline-none" data-testid="views-menu-button" :class="{ pressed: open }">
          <dynamic-svg class="bar-icon" src="/svg/stand.svg" />
          <span>{{ $t('session_views') }}</span>
        </PopoverButton>

        <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="translate-y-0" leave-to-class="opacity-0 translate-y-1" @before-enter="emit('views-menu-state', true)" @after-leave="emit('views-menu-state', false)">
          <PopoverPanel class="absolute left-0 z-[1000] mt-1 flex w-screen max-w-max bg-transparent" v-slot="{ close }">
            <div class="w-screen max-w-sm flex-auto rounded-[16px] p-1 text-md bg-layer-3 p-1">
                <button class="font-semibold cursor-pointer text-md flex gap-2 p-3 items-center fill-text-neutral-low"  v-for="item in views" :key="item.icon"
                  :class="view === item.code ? 'text-primary': 'text-text-neutral-high'" :data-testid="`view-option-${item.code}`" @click.prevent="() => { handleViewSelect(item.code, close) }">
                  <dynamic-svg :src="item.icon" width="16" height="16" fill="currentColor" fill-opacity="1" />
                  {{ $t(item.label) }}
                </button>
            </div>
          </PopoverPanel>
        </transition>
      </Popover>
    </div>
    <div class="ml-0 md:ml-auto flex items-center justify-between md:justify-center flex-1 md:flex-none">
      <div class="flex md:flex-[52px]">
        <TooltipWrap hover :content="$t('invite_people_tooltip')" :arrow="false">
          <button class="top-bar-button hover:bg-button-ghost-mono-active" data-testid="people-button" @click="emit('people')">
            <span class="flex gap-[2px] items-center">
              <img class="bar-icon" src="@/assets/icons/sorenson/users-plus.svg" alt="People">
              <span class="top-bar-badge px-[3px] bg-text-neutral-low rounded-[8px] text-black text-[10px]/4+  font-semibold min-w-4 h-4">{{ people }}</span>
            </span>
            <span>{{ $t('session_button_people') }}</span>
          </button>
        </TooltipWrap>
      </div>

      <div class="flex md:flex-[52px]">
        <button class="top-bar-button hover:bg-button-ghost-mono-active"  @click="emit('language')">
          <img :src="'/data/flags/' + language + '.png'" class="w-6 h-6 overflow-hidden rounded-full" alt="">
          <span>{{ $t('profile_language') }}</span>
        </button>
      </div>

      <div class="flex md:hidden">
        <Popover class="relative md:flex-[52px]" v-slot="{ open }">
          <PopoverButton class="top-bar-button hover:bg-button-ghost-mono-active outline-none" :class="{ pressed: open }">
            <img class="bar-icon" src="@/assets/icons/sorenson/settings-slider.svg" alt="Settings">
            <span>{{ $t('settings_title') }}</span>
          </PopoverButton>

          <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="translate-y-0" leave-to-class="opacity-0 translate-y-1" @before-enter="emit('views-menu-state', true)" @after-leave="emit('views-menu-state', false)">
            <PopoverPanel class="absolute -left-20 z-[1000] mt-1 w-screen max-w-max bg-transparent" v-slot="{ close }">
              <div class="w-full flex-auto rounded-[16px] bg-layer-3 p-4">
                <h3 class="text-[14px] font-semibold text-text-neutral-low mb-2">{{ $t('settings_title') }}</h3>
                <div class="h-[1px] bg-white/10 mb-3 -mx-4"></div>

                <!-- Settings Options -->
                <button class="flex flex-row justify-between w-full items-center gap-12 text-[16px] font-medium text-text-neutral-high mb-3" data-testid="mobile-caption-settings" @click.prevent="() => { emit('caption-settings-bar'); close(); }">
                  <span>{{ captionSettingsLabel }}</span>
                  <dynamic-svg :src="'/svg/chevron-left.svg'" width="20" height="20" fill="currentColor" fill-opacity="1" />
                </button>

                <button class="flex flex-row justify-between w-full items-center gap-12 text-[16px] font-medium text-text-neutral-high mb-3" data-testid="mobile-session-settings" @click.prevent="() => { emit('settings'); close(); }">
                  <span>{{ sessionSettingsLabel }}</span>
                  <dynamic-svg :src="'/svg/chevron-left.svg'" width="20" height="20" fill="currentColor" fill-opacity="1" />
                </button>

                <h3 class="text-[14px] font-semibold text-text-neutral-low mb-2">{{ $t('session_views') }}</h3>
                <div class="h-[1px] bg-white/10 mb-3 -mx-4"></div>
                
                <!-- Views Options -->
                <button class="font-semibold cursor-pointer text-md flex gap-2 p-3 items-center fill-text-neutral-low"  v-for="item in views" :key="item.icon"
                  :class="view === item.code ? 'text-primary': 'text-text-neutral-high'" :data-testid="`mobile-view-option-${item.code}`" @click.prevent="() => { handleViewSelect(item.code, close) }">
                  <dynamic-svg :src="item.icon" width="16" height="16" fill="currentColor" fill-opacity="1" />
                  {{ $t(item.label) }}
                </button>
              </div>
            </PopoverPanel>
          </transition>
        </Popover>
      </div>

      <div class="hidden md:flex flex-[52px]">
        <Popover class="relative" v-slot="{ open }">
          <PopoverButton class="top-bar-button hover:bg-button-ghost-mono-active outline-none" data-testid="settings-menu-button" :class="{ pressed: open }">
            <img class="bar-icon" src="@/assets/icons/sorenson/settings-slider.svg" alt="Settings">
            <span>{{ $t('settings_title') }}</span>
          </PopoverButton>

          <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="translate-y-0" leave-to-class="opacity-0 translate-y-1" @before-enter="emit('views-menu-state', true)" @after-leave="emit('views-menu-state', false)">
            <PopoverPanel class="absolute right-0 z-[1000] mt-1 flex w-screen max-w-max bg-transparent" v-slot="{ close }">
              <div class="flex-auto rounded-[16px] p-1 text-md bg-layer-3">
                <button class="w-full font-semibold cursor-pointer text-md flex p-3 items-center text-text-neutral-high whitespace-nowrap" data-testid="caption-settings-option" @click.prevent="() => { emit('caption-settings'); close(); }">
                  {{ captionSettingsLabel }}
                </button>
                <button class="w-full font-semibold cursor-pointer text-md flex p-3 items-center text-text-neutral-high whitespace-nowrap" data-testid="session-settings-option" @click.prevent="() => { emit('session-settings'); close(); }">
                  {{ sessionSettingsLabel }}
                </button>
                <button class="w-full font-semibold cursor-pointer text-md flex p-3 items-center text-text-neutral-high whitespace-nowrap" data-testid="keyboard-shortcuts-option" @click.prevent="() => { emit('shortcuts'); close(); }">
                  {{ shortcutsLabel }}
                </button>
              </div>
            </PopoverPanel>
          </transition>
        </Popover>
      </div>



      <div class="ml-0 md:ml-3 flex md:flex-[52px] md:flex-auto">
        <TooltipWrap hover :content="$t('leave_or_end_session_tooltip')" :arrow="false">
          <button class="min-w-[64px] top-bar-button top-bar-button--leave bg-button-secondary-destructive-resting hover:bg-button-secondary-destructive-active"  @click="emit('leave')">
            <img src="@/assets/icons/sorenson/align-right-01.svg" alt="Leave">
            <span class="text-text-neutral-red">{{ $t('session_leave') }}</span>
          </button>
        </TooltipWrap>
      </div>
    </div>
  </div>
</template>

<script setup>

import { computed, defineEmits, defineProps } from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import DynamicSvg from "@/components/ui/DynamicSvg.vue";
import TooltipWrap from "@/components/ui/TooltipWrap.vue";
import { i18n } from "@/plugins/i18n";

defineProps({
  language: {
    type: String,
    required: true,
  },
  people: {
    type: Number,
    required: false,
    default: 0,
  },
  view: {
    type: String,
    required: false,
    default: 'default',
  },
})

const views = [
  {
    label:'session_views_default',
    icon: '/svg/chat.svg',
    code: "default"
  },
  {
    label:'Green Screen',
    icon: '/svg/chromakey.svg',
    code: "chromakey"
  }
];

// Translations come from the server, so fall back to English until a key exists there.
const translate = (key, fallback) => {
  const text = i18n.global.t(key)
  return text !== key ? text : fallback
}

const captionSettingsLabel = computed(() => translate('caption_settings', 'Caption Settings'))
const sessionSettingsLabel = computed(() => translate('session_settings', 'Session Settings'))
const shortcutsLabel = computed(() => translate('shortcuts_title', 'Keyboard shortcuts'))

const emit = defineEmits(['leave', 'language', 'settings', 'session-settings', 'people', 'view', 'views-menu-state', 'caption-settings', 'caption-settings-bar', 'shortcuts'])

const handleViewSelect = (code, close) => {
  emit('view', code)
  close() // закрывает Popover
}
</script>
